import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { BadgeDollarSign, ChevronDown, Eye, EyeOff, PartyPopper, TicketPercent } from "lucide-react";

function formatErr(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

function AuthShell({ heading, children, mode = "signup" }) {
  return (
    <div className="min-h-screen bg-[var(--auth-canvas-deep)]">
      <div className="flex min-h-[56px] items-center justify-center bg-shop-body px-4 text-center text-sm font-medium text-white sm:text-base">
        Earn new seller incentives worth up to $23,630
      </div>

      <header className="flex h-[68px] items-center justify-between bg-black px-5 sm:px-8 lg:px-11">
        <Link to="/" className="flex items-center gap-2.5" aria-label="Seller Center home">
          <img src="/logo.svg?v=2" alt="" className="h-7 w-7 object-contain" />
          <span className="font-head text-xl font-bold text-white sm:text-2xl">Seller Center</span>
        </Link>
        <div className="flex items-center gap-4 sm:gap-6">
          <span className="hidden items-center gap-1 text-sm font-semibold text-white md:flex">United States <ChevronDown size={14} /></span>
          <Link to={mode === "signup" ? "/login" : "/register"} className="text-sm font-semibold text-white underline-offset-4 hover:underline">
            {mode === "signup" ? "Log in" : "Join now"}
          </Link>
          {mode === "signup" && (
            <Link to="/register" className="hidden rounded bg-brand px-5 py-2.5 text-sm font-semibold text-white sm:inline-flex">Join now</Link>
          )}
        </div>
      </header>

      <main className="flex min-h-[calc(100vh-124px)] items-center bg-[radial-gradient(circle_at_65%_45%,var(--auth-canvas),var(--auth-canvas-deep)_66%)] px-5 py-10 sm:px-8 lg:py-14">
        <div className="mx-auto grid w-full max-w-[1260px] items-center gap-12 lg:grid-cols-[1.08fr_0.92fr]">
          <section className="hidden text-[#ffffff] lg:block">
            <h2 className="max-w-[600px] font-head text-[52px] font-extrabold leading-[1.08]">
              Sell more with up to $23,630 in <span className="text-brand">new seller rewards</span>
            </h2>
            <div className="mt-8 space-y-4 text-xl">
              <p className="flex items-center gap-4"><PartyPopper size={25} /> Sell where discovery drives sales</p>
              <p className="flex items-center gap-4"><BadgeDollarSign size={25} /> Start your shop with $0 setup fees</p>
              <p className="flex items-center gap-4"><TicketPercent size={25} /> Earn rewards to accelerate growth</p>
            </div>
          </section>

          <section className="mx-auto w-full max-w-[555px] rounded-[30px] bg-surface px-6 py-8 sm:px-10 sm:py-10">
            <h1 className="font-head text-[32px] font-bold text-shop-ink">{heading}</h1>
            <div className="mt-8">{children}</div>
          </section>
        </div>
      </main>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="mb-5">
      <label className="mb-2 block text-base font-medium text-shop-ink">{label}</label>
      {children}
    </div>
  );
}

const inputCls =
  "h-[52px] w-full rounded border border-line bg-surface px-4 text-base text-shop-ink placeholder:text-shop-muted outline-none transition-[border-color,box-shadow] duration-150 focus:border-shop-ink focus:shadow-[0_0_0_1px_var(--shop-ink)]";

export function Login() {
  const { login } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email.trim(), password, "merchant");
      navigate("/seller");
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };


  return (
    <AuthShell heading={t("auth.loginTitle")} mode="login">
      <form onSubmit={submit} className="animate-fade-up">
        <Field label={t("auth.phoneEmail")}>
          <input
            className={inputCls}
            placeholder={t("auth.emailPh")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            data-testid="login-email"
          />
        </Field>

        <Field label={t("auth.enterPassword")}>
          <div className="relative">
            <input
              className={inputCls + " pr-12"}
              type={show ? "text" : "password"}
              placeholder={t("auth.loginPasswordPh")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="login-password"
            />
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-muted hover:text-white"
              data-testid="toggle-password"
            >
              {show ? <Eye size={20} /> : <EyeOff size={20} />}
            </button>
          </div>
        </Field>

        <div className="mb-1">
          <div className="text-[15px] text-shop-body">
            {t("auth.noAccount")}{" "}
            <Link to="/register" className="text-brand hover:underline" data-testid="register-nav">
              {t("auth.signup")}
            </Link>
          </div>
        </div>


        {err && (
          <div className="mt-4 rounded-lg border border-brand/40 bg-brand/10 px-3 py-2 text-sm text-brand" data-testid="login-error">
            {err}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          data-testid="login-submit"
          className="mt-8 w-full rounded bg-brand py-3.5 text-base font-bold text-white transition-[background-color,transform] duration-150 hover:bg-shop-primary-dark active:scale-[0.99] disabled:opacity-60"
        >
          {loading ? t("auth.loggingIn") : t("auth.loginBtn")}
        </button>



      </form>
    </AuthShell>
  );
}

export function Register() {
  const { register } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await register(name.trim(), email.trim(), password);
      navigate("/seller");
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell heading={t("auth.signupTitle")}>
      <form onSubmit={submit} className="animate-fade-up">
        <Field label={t("auth.name")}>
          <input
            className={inputCls}
            placeholder={t("auth.namePh")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            data-testid="register-name"
          />
        </Field>

        <Field label={t("auth.phoneEmail")}>
          <input
            className={inputCls}
            placeholder={t("auth.emailPh")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="text"
            inputMode="text"
            autoComplete="username"
            required
            data-testid="register-email"
          />
        </Field>

        <Field label={t("auth.setPassword")}>
          <div className="relative">
            <input
              className={inputCls + " pr-12"}
              type={show ? "text" : "password"}
              placeholder={t("auth.setPasswordPh")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="register-password"
            />
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-muted hover:text-white"
            >
              {show ? <Eye size={20} /> : <EyeOff size={20} />}
            </button>
          </div>
        </Field>

        <div className="text-[15px] text-shop-body">
          {t("auth.already")}{" "}
          <Link to="/login" className="text-brand hover:underline">{t("auth.login")}</Link>
        </div>

        {err && (
          <div className="mt-4 rounded-lg border border-brand/40 bg-brand/10 px-3 py-2 text-sm text-brand" data-testid="register-error">
            {err}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          data-testid="register-submit"
          className="mt-8 w-full rounded bg-brand py-3.5 text-base font-bold text-white transition-[background-color,transform] duration-150 hover:bg-shop-primary-dark active:scale-[0.99] disabled:opacity-60"
        >
          {loading ? t("auth.creating") : t("auth.signupTitle")}
        </button>
      </form>
    </AuthShell>
  );
}

export function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email.trim(), password, "admin");
      navigate("/admin");
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell heading="Control Panel">
      <form onSubmit={submit} className="animate-fade-up">
        <Field label="Email">
          <input
            className={inputCls}
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            data-testid="admin-login-email"
          />
        </Field>

        <Field label="Password">
          <div className="relative">
            <input
              className={inputCls + " pr-12"}
              type={show ? "text" : "password"}
              placeholder="Enter password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="admin-login-password"
            />
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-muted hover:text-white"
            >
              {show ? <Eye size={20} /> : <EyeOff size={20} />}
            </button>
          </div>
        </Field>

        {err && (
          <div className="mt-4 rounded-lg border border-brand/40 bg-brand/10 px-3 py-2 text-sm text-brand" data-testid="admin-login-error">
            {err}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="mt-8 w-full rounded-lg bg-brand py-4 text-lg font-bold tracking-wide text-white shadow-[0_2px_10px_-2px_rgba(254,44,85,0.5)] transition-[background-color,transform] duration-150 hover:bg-[#e01f47] active:scale-[0.99] disabled:opacity-60"
        >
          {loading ? "Signing in..." : "Sign in"}
        </button>
      </form>
    </AuthShell>
  );
}
